// src/pages/About.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Target,
  Users,
  Globe,
  Award,
  Heart,
  Zap,
  BarChart,
  Wallet,
  Lock,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Key,
  Cpu,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";
import AOS from "aos";
import "aos/dist/aos.css";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

const About: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const heroTexts = [
    {
      main: "Advanced Crypto Protection",
      highlight: "For Modern Investors",
      color: "from-primary to-primary/80",
    },
    {
      main: "Expert Recovery Solutions",
      highlight: "When Assets Are Compromised",
      color: "from-blue-500 to-blue-400",
    },
    {
      main: "Innovative Security Technology",
      highlight: "For Digital Wealth",
      color: "from-green-500 to-green-400",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
        setFade(true);
      }, 500);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 500,
      easing: "ease-in-out-cubic",
      once: false,
    });
  }, []);

  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/about" />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-5 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div
              className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Trusted Since 2022
            </div>

            {/* Rotating Hero Text */}
            <div className="h-48 lg:h-40 mb-8">
              <div
                className={`transition-all duration-700 ${
                  fade
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-4"
                }`}
              >
                <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white mb-8">
                  {heroTexts[currentTextIndex].main}
                  <span
                    className={`block bg-gradient-to-r ${heroTexts[currentTextIndex].color} bg-clip-text text-transparent`}
                  >
                    {heroTexts[currentTextIndex].highlight}
                  </span>
                </h1>
              </div>
            </div>

            <p
              className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              SyvronRecovery pioneers the integration of AI-driven market
              protection with expert blockchain forensic services, creating a
              comprehensive security ecosystem for cryptocurrency investors
              worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
                Our Founding Vision
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p data-aos="fade-right" data-aos-delay="200">
                  Established in 2022 by cybersecurity pioneers and blockchain
                  experts, SyvronRecovery emerged from a critical need:
                  cryptocurrency investors lacked sophisticated protection
                  against market volatility and security threats.
                </p>
                <p data-aos="fade-right" data-aos-delay="300">
                  Witnessing substantial losses from market corrections and
                  sophisticated scams, our founders engineered a dual-layered
                  defense system: proactive AI-driven market protection
                  complemented by expert forensic recovery services.
                </p>
                <p data-aos="fade-right" data-aos-delay="400">
                  Today, we've evolved into a global security platform,
                  safeguarding thousands of digital portfolios and successfully
                  recovering millions in compromised assets across multiple
                  blockchain networks.
                </p>
              </div>
            </div>
            <div
              className="grid grid-cols-2 gap-6"
              data-aos="fade-left"
              data-aos-delay="500"
            >
              {/* Mission Card */}
              <div
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-primary transition-all duration-300 hover:scale-102 group/card"
                data-aos="zoom-in"
                data-aos-delay="100"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Our Mission
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Revolutionize crypto security through intelligent protection
                  and expert recovery solutions
                </p>
              </div>

              {/* Global Card */}
              <div
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-blue-500 transition-all duration-300 hover:scale-102 group/card"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <Globe className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Global Operations
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Serving investors across{" "}
                  {statsInView && <CountUp end={50} suffix="+" duration={2} />}{" "}
                  countries with 24/7 support
                </p>
              </div>

              {/* Expertise Card */}
              <div
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-green-500 transition-all duration-300 hover:scale-102 group/card"
                data-aos="zoom-in"
                data-aos-delay="300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-green-500/10 to-green-500/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <Award className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Expert Team
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Certified blockchain security specialists and forensic
                  analysts
                </p>
              </div>

              {/* Community Card */}
              <div
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-purple-500 transition-all duration-300 hover:scale-102 group/card"
                data-aos="zoom-in"
                data-aos-delay="400"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <Users className="h-6 w-6 text-purple-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Community Focus
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Built by crypto enthusiasts for the global digital asset
                  community
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-900/30">
        <div className="container mx-auto px-4 lg:px-5">
          <div
            className="text-center mb-12 lg:mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
              Core Principles
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              The foundational values that drive every decision and innovation
              at SyvronRecovery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Security Value */}
            <div
              className="text-center group/value"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="relative mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-full blur-lg"></div>
                <div className="relative w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/value:scale-110 transition-transform">
                  <Shield className="h-10 w-10 text-primary" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Maximum Security
              </h3>
              <p className="text-gray-600 dark:text-gray-400 px-4">
                Implementing enterprise-grade encryption, zero-knowledge proofs,
                and continuous security audits to protect your digital assets
              </p>
            </div>

            {/* Client Success Value */}
            <div
              className="text-center group/value"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="relative mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-green-500/10 rounded-full blur-lg"></div>
                <div className="relative w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/value:scale-110 transition-transform">
                  <Heart className="h-10 w-10 text-green-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Client Success
              </h3>
              <p className="text-gray-600 dark:text-gray-400 px-4">
                Measuring our achievements by your financial security, recovered
                assets, and overall investment confidence
              </p>
            </div>

            {/* Innovation Value */}
            <div
              className="text-center group/value"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="relative mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-blue-500/10 rounded-full blur-lg"></div>
                <div className="relative w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/value:scale-110 transition-transform">
                  <Zap className="h-10 w-10 text-blue-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Continuous Innovation
              </h3>
              <p className="text-gray-600 dark:text-gray-400 px-4">
                Developing cutting-edge technologies and strategies to
                anticipate emerging threats and market challenges
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
                Advanced Protection Technology
              </h2>
              <div className="space-y-6">
                {/* Market Analysis Feature */}
                <div
                  className="flex items-start group/feature"
                  data-aos="fade-right"
                  data-aos-delay="200"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/feature:scale-110 transition-transform">
                    <Cpu className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      AI-Powered Market Analysis
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Our neural networks continuously analyze market patterns,
                      identifying potential downturns with predictive accuracy
                      to trigger protective measures
                    </p>
                  </div>
                </div>

                {/* Wallet Integration Feature */}
                <div
                  className="flex items-start group/feature"
                  data-aos="fade-right"
                  data-aos-delay="300"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500/10 to-green-500/5 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/feature:scale-110 transition-transform">
                    <Wallet className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Secure Wallet Integration
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      End-to-end encrypted connections with multi-signature
                      security protocols that monitor without storing sensitive
                      private key information
                    </p>
                  </div>
                </div>

                {/* Multi-Layer Security Feature */}
                <div
                  className="flex items-start group/feature"
                  data-aos="fade-right"
                  data-aos-delay="400"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/feature:scale-110 transition-transform">
                    <Lock className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Multi-Layer Security Architecture
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Military-grade encryption combined with hardware security
                      modules and zero-knowledge proofs to ensure maximum data
                      protection
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Technology Card */}
            <div className="relative" data-aos="fade-left" data-aos-delay="500">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-6 lg:p-8">
                <div className="space-y-6">
                  {/* Protection Protocol Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 group/card hover:scale-102 transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="100"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-primary/20 group-hover/card:scale-110 transition-transform">
                        <BarChart className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                          Intelligent Protection Protocol
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Automated security measures activate when market
                          indicators reach predefined risk thresholds
                        </p>
                      </div>
                      <Key className="h-4 w-4 text-primary opacity-0 group-hover/card:opacity-100 transition-opacity" />
                    </div>
                  </div>

                  {/* Recovery Success Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-green-500/10 to-green-500/5 border border-green-500/20 group/card hover:scale-102 transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-green-500/20 group-hover/card:scale-110 transition-transform">
                        <ShieldCheck className="h-6 w-6 text-green-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                          Recovery Success Rate
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span ref={statsRef}>
                            {statsInView && (
                              <CountUp end={78} suffix="%" duration={2} />
                            )}
                          </span>{" "}
                          success rate in retrieving compromised funds across
                          various scenarios
                        </p>
                      </div>
                      <Sparkles className="h-4 w-4 text-green-500 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                    </div>
                  </div>

                  {/* Network Support Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/20 group/card hover:scale-102 transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-blue-500/20 group-hover/card:scale-110 transition-transform">
                        <Globe className="h-6 w-6 text-blue-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                          Multi-Network Support
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Comprehensive coverage across{" "}
                          {statsInView && (
                            <CountUp end={25} suffix="+" duration={2} />
                          )}{" "}
                          leading blockchain networks including major platforms
                        </p>
                      </div>
                      <Zap className="h-4 w-4 text-blue-500 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-900/30">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 rounded-3xl blur-xl opacity-50"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8 lg:p-12">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {[
                  {
                    value: 2022,
                    label: "Year Established",
                    color: "text-primary",
                    delay: 100,
                  },
                  {
                    value: 5000,
                    suffix: "+",
                    label: "Protected Portfolios",
                    color: "text-green-500",
                    delay: 200,
                  },
                  {
                    value: 42,
                    suffix: "+",
                    label: "Expert Team",
                    color: "text-blue-500",
                    delay: 300,
                  },
                  {
                    value: 24,
                    suffix: "/7",
                    label: "Security Monitoring",
                    color: "text-purple-500",
                    delay: 400,
                  },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="text-center group/stat"
                    data-aos="zoom-in"
                    data-aos-delay={stat.delay}
                  >
                    <div
                      className={`text-4xl lg:text-5xl font-bold ${stat.color} mb-2 group-hover/stat:scale-105 transition-transform`}
                    >
                      {statsInView && (
                        <CountUp
                          end={stat.value}
                          suffix={stat.suffix}
                          duration={2.5}
                        />
                      )}
                    </div>
                    <div className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div
              className="relative bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 lg:p-12 text-center backdrop-blur-sm border border-primary/30"
              data-aos="fade-up"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Join Our Security Revolution
              </h2>
              <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
                Whether you're securing existing assets or need expert recovery
                assistance, SyvronRecovery provides the comprehensive protection
                you need
              </p>
              <div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <Link
                  to="/register"
                  className="bg-white text-primary hover:bg-gray-100 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center group/cta"
                >
                  <span>Start Protecting Assets</span>
                  <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover/cta:translate-x-1" />
                </Link>
                <Link
                  to="/login"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
                >
                  Existing User Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="SyvronRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  SyvronRecovery
                </span>
              </div>
              <p className="mb-4 text-sm">
                Advanced crypto protection and recovery services combining AI
                technology with expert human oversight.
              </p>
              <div className="flex space-x-3">
                <Globe className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Users className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Lock className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/wallet"
                    className="hover:text-white transition-colors"
                  >
                    Wallet Protection
                  </Link>
                </li>
                <li>
                  <Link
                    to="/recovery"
                    className="hover:text-white transition-colors"
                  >
                    Fund Recovery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard"
                    className="hover:text-white transition-colors"
                  >
                    Dashboard
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="hover:text-white transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2 text-sm">support@syvronrecovery.com</p>
              <p className="text-sm">24/7 Expert Support Available</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>
              © {new Date().getFullYear()} SyvronRecovery. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
